import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import BDPLayout from "../../components/bdp/BDPLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { DollarSign, TrendingUp, CheckCircle, LinkIcon, Clock, Trophy, Target, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const BDPDashboard = () => {
  const { API, token, user } = useAuth();
  const [data, setData] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API}/bdp/dashboard`, { headers: h }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/bdp/leaderboard`, { headers: h }).then(r => r.ok ? r.json() : null),
    ]).then(([d, lb]) => {
      setData(d);
      setLeaderboard(lb);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [API, token]);

  if (loading) return <BDPLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500" /></div></BDPLayout>;

  const stats = [
    { label: "Total Sales", value: `$${(data?.total_sales || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`, icon: DollarSign, gradient: "from-emerald-500 to-teal-600" },
    { label: "Commission Earned", value: `$${(data?.total_commission || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`, icon: TrendingUp, gradient: "from-blue-500 to-indigo-600" },
    { label: "Deals Closed", value: data?.deals_closed || 0, icon: CheckCircle, gradient: "from-purple-500 to-violet-600" },
    { label: "Outstanding Links", value: data?.outstanding_links || 0, icon: LinkIcon, gradient: "from-amber-500 to-orange-600" },
  ];

  const myCode = user?.agent_code;
  const rankColors = ["text-yellow-500", "text-gray-400", "text-amber-700"];

  const MiniLeaderboard = ({ title, icon: Icon, entries, valueKey, fmt }) => (
    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Icon className="w-4 h-4 text-white" />
            </div>
            <h3 className="font-semibold text-[#1A2636] text-sm">{title}</h3>
          </div>
          <Link to="/sales/leaderboard" className="text-xs text-emerald-600 hover:underline flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></Link>
        </div>
        <div className="space-y-2">
          {(entries || []).slice(0, 5).map((e, i) => {
            const isMe = e.agent_code === myCode;
            return (
              <div key={e.agent_code} className={`flex items-center justify-between py-2 px-3 rounded-lg ${isMe ? "bg-emerald-50 ring-1 ring-emerald-200" : i < 3 ? "bg-gray-50" : ""}`}>
                <div className="flex items-center gap-2.5">
                  <span className={`text-sm font-bold w-5 ${i < 3 ? rankColors[i] : "text-gray-400"}`}>
                    {i === 0 ? "1" : i === 1 ? "2" : i === 2 ? "3" : i + 1}
                  </span>
                  <span className="text-sm font-medium text-[#1A2636]">
                    {e.name}
                    {isMe && <Badge className="ml-1.5 bg-emerald-100 text-emerald-700 text-[9px] px-1 py-0">You</Badge>}
                  </span>
                </div>
                <span className="text-sm font-bold text-[#1A2636]">{fmt(e[valueKey])}</span>
              </div>
            );
          })}
          {(!entries || entries.length === 0) && <p className="text-xs text-gray-400 text-center py-3">No data yet</p>}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <BDPLayout>
      <div className="space-y-6" data-testid="bdp-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#1A2636]">Welcome back, {user?.name?.split(" ")[0]}</h1>
            <p className="text-sm text-gray-500 mt-0.5">Agent Code: <span className="font-mono font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">{data?.agent_code}</span></p>
          </div>
          <div className="flex gap-2">
            <Link to="/sales/payment-links"><Button className="bg-emerald-600 hover:bg-emerald-700 gap-2 text-sm h-9"><LinkIcon className="w-3.5 h-3.5" /> New Payment Link</Button></Link>
            <Link to="/sales/commissions"><Button variant="outline" className="gap-2 text-sm h-9"><TrendingUp className="w-3.5 h-3.5" /> Commissions</Button></Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <Card key={s.key || s.name || s.title || i} className="border-0 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
              <CardContent className="p-0">
                <div className="flex items-center gap-4 p-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${s.gradient} flex items-center justify-center shrink-0`}>
                    <s.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-gray-400 font-medium">{s.label}</p>
                    <p className="text-xl font-bold text-[#1A2636] mt-0.5">{s.value}</p>
                  </div>
                </div>
                <div className={`h-1 bg-gradient-to-r ${s.gradient}`} />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Leaderboards + Recent Deals */}
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Leaderboards */}
          <MiniLeaderboard
            title="Sales Volume"
            icon={DollarSign}
            entries={leaderboard?.by_sales}
            valueKey="total_sales"
            fmt={v => `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
          />
          <MiniLeaderboard
            title="Deals Closed"
            icon={Target}
            entries={leaderboard?.by_deals}
            valueKey="deals_closed"
            fmt={v => `${v}`}
          />

          {/* Recent deals */}
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="font-semibold text-[#1A2636] text-sm">Recent Deals</h3>
                </div>
                <Link to="/sales/payment-links" className="text-xs text-emerald-600 hover:underline flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></Link>
              </div>
              {data?.recent_deals?.length ? (
                <div className="space-y-2.5">
                  {data.recent_deals.map((deal) => (
                    <div key={deal.link_id} className="flex items-center justify-between py-2">
                      <div>
                        <p className="text-sm font-medium text-[#1A2636]">{deal.client_name}</p>
                        <p className="text-[11px] text-gray-400">{deal.engagement_type === "monthly" ? "Monthly" : "One-time"} &middot; {new Date(deal.paid_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-[#1A2636]">${Number(deal.amount).toLocaleString("en-US", { minimumFractionDigits: 0 })}</p>
                        <p className="text-[11px] text-emerald-600 font-medium">+${Number(deal.commission_amount).toLocaleString("en-US", { minimumFractionDigits: 0 })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-400 text-sm">
                  <Clock className="w-7 h-7 mx-auto mb-2 opacity-40" />
                  No deals yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </BDPLayout>
  );
};

export default BDPDashboard;
